import React, { memo, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary/ui';

import type { MappedGlobalShortcutsItem } from '../../types';
import { getUrlMaybe } from '../../utils/get-url-maybe';
import { MenuComponentItem } from '../menu-component-item';
import { fireMenuLinkItemClickAnalyticsEvent, MenuLinkItem } from '../menu-link-item';
import { menuErrorMessages, messages } from './messages';

export type ShortcutMenuItemProps = {
	// This component is exported from the package, which means we can't rely on the
	// context provider that Global Shortcuts uses to determine if Drag & Drop is enabled,
	// as consumers might be using something else.
	// Thus, this value needs to be provided as a prop by whatever is rendering it.
	dragAndDropEnabled?: boolean;
	isInMoreMenu?: boolean;
	openInSameTab?: boolean;
	product?: string;
	shortcut: MappedGlobalShortcutsItem;
};

export const ShortcutMenuItem: React.NamedExoticComponent<ShortcutMenuItemProps> =
	memo(ShortcutMenuItemInner);

export function ShortcutMenuItemInner(props: ShortcutMenuItemProps): React.JSX.Element {
	const menuId = props.shortcut.key;
	const errorMessageValues = useMemo(() => ({ menuId }), [menuId]);

	return (
		<NavErrorBoundary
			key={menuId}
			errorMessages={menuErrorMessages}
			errorMessageValues={errorMessageValues}
			showFlags
		>
			<ShortcutMenuItemWithoutErrorBoundary {...props} />
		</NavErrorBoundary>
	);
}

function ShortcutMenuItemWithoutErrorBoundary({
	shortcut,
	dragAndDropEnabled,
	product,
	openInSameTab,
	isInMoreMenu,
}: ShortcutMenuItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const labelText: string = shortcut.label;

	const analyticsName = shortcut.analyticsName || `unknown_${labelText}`;
	const ItemWrapper = shortcut?.ItemWrapper ?? React.Fragment;

	const customComponentDraggableOnClick = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [analyticsName, createAnalyticsEvent]);

	if (shortcut.type === 'component') {
		return (
			<MenuComponentItem
				shortcut={shortcut}
				dragAndDropEnabled={dragAndDropEnabled}
				isInMoreMenu={isInMoreMenu}
				onClick={customComponentDraggableOnClick}
			/>
		);
	}

	if (shortcut.type === 'internal') {
		return (
			<ItemWrapper>
				<MenuLinkItem
					analyticsName={analyticsName}
					key={shortcut.key}
					icon={shortcut.icon}
					isSelected={shortcut.isSelected}
					isReorderable={shortcut.isReorderable}
					label={labelText}
					labelElem={shortcut.labelElem}
					openInSameTab={true}
					onClick={shortcut.onClick}
					onRightClick={shortcut.onRightClick}
					product={product}
					url={getUrlMaybe(shortcut)}
				/>
			</ItemWrapper>
		);
	}

	return (
		<ItemWrapper>
			<MenuLinkItem
				actions={shortcut.actions}
				analyticsName={analyticsName}
				ariaLabel={formatMessage(messages.menuLinkItemAriaLabel, {
					appName: labelText,
					siteName: '',
				})}
				icon={shortcut.icon}
				isReorderable={shortcut.isReorderable}
				key={shortcut.key}
				label={labelText}
				labelElem={shortcut.labelElem}
				menuItemId={shortcut.menuItemId}
				onClick={shortcut.onClick}
				onFocus={shortcut.onFocus}
				onMouseEnter={shortcut.onMouseEnter}
				onRightClick={shortcut.onRightClick}
				openInSameTab={openInSameTab}
				optionalRef={shortcut.ref}
				optionsMenuElem={shortcut.optionsMenuElem}
				product={product}
				url={getUrlMaybe(shortcut)}
			/>
		</ItemWrapper>
	);
}
